import type { MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'
import { db } from '@afficheservices/db'
import { sql } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { TASKS_BY_SERVICE, DEAD_TASK_SLUGS } from '~/lib/tarifs-tasks-data'
import { SERVICE_LABELS } from '~/lib/programmatic-seo'
import { Euro, ArrowRight } from 'lucide-react'

/**
 * /tarifs — the hub that never existed.
 *
 * ~13 700 /tarifs/[service]/[city]/[task] pages were submitted in sitemaps while
 * /tarifs and /tarifs/[service] both returned 404, so the only way in was the
 * sitemap plus a handful of sibling links on the leaf pages themselves. GSC
 * 2026-08: /tarifs is the single largest bucket in "Crawled — currently not
 * indexed" (~30% of 34 669). Pages with no hub above them read as orphans.
 */
export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: `Tarifs travaux ${new Date().getFullYear()} — Prix par prestation et par ville | AfficheServices` },
  { name: 'description', content: `Grilles de prix pour ${data?.taskTotal ?? 0} prestations dans ${data?.services?.length ?? 0} métiers : fourchette de tarif, ce qui fait varier le devis, et le prix constaté ville par ville.` },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/tarifs' },
  { property: 'og:title', content: 'Tarifs travaux — Prix par prestation et par ville' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/tarifs' },
]

export async function loader() {
  // Count the pages that actually exist per service, so the hub advertises real
  // coverage instead of the theoretical service × city × task matrix.
  const rows = (await db.execute(sql`
    SELECT split_part(path, '/', 3) AS service, COUNT(*)::int AS n
    FROM seo_index_whitelist
    WHERE path LIKE '/tarifs/%'
    GROUP BY 1
  `)) as unknown as Array<{ service: string; n: number }>
  const cityCounts = new Map(rows.map((r) => [r.service, r.n]))

  const services = Object.entries(TASKS_BY_SERVICE)
    .map(([slug, tasks]) => {
      const live = tasks.filter((t) => !DEAD_TASK_SLUGS.has(t.slug))
      return {
        slug,
        name: SERVICE_LABELS[slug]?.name ?? slug,
        artisan: SERVICE_LABELS[slug]?.artisan ?? slug,
        tasks: live.map((t) => ({ slug: t.slug, label: t.label, priceRange: t.priceRange })),
        pageCount: cityCounts.get(slug) ?? 0,
      }
    })
    .filter((s) => s.tasks.length > 0)
    .sort((a, b) => b.pageCount - a.pageCount)

  return { services, taskTotal: services.reduce((n, s) => n + s.tasks.length, 0) }
}

export default function TarifsIndexPage() {
  const { services, taskTotal } = useLoaderData<typeof loader>()
  const year = new Date().getFullYear()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Tarifs', url: 'https://www.afficheservices.fr/tarifs' },
      ]} />

      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-emerald-200">
            <Euro className="h-3.5 w-3.5" /> Grilles de prix {year}
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">Combien coûtent vos travaux&nbsp;?</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            {taskTotal} prestations chiffrées, prestation par prestation et ville par ville :
            la fourchette de prix, ce qui la fait bouger, et les artisans qui interviennent près de chez vous.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        <div className="space-y-8">
          {services.map((s) => (
            <section key={s.slug}>
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="text-lg font-bold text-slate-900">
                  <Link to={`/tarifs/${s.slug}`} className="hover:text-primary">Tarifs {s.name.toLowerCase()}</Link>
                </h2>
                {s.pageCount > 0 && (
                  <span className="shrink-0 text-xs text-slate-500">{s.pageCount} villes chiffrées</span>
                )}
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {s.tasks.map((t) => (
                  <Link
                    key={t.slug}
                    to={`/tarifs/${s.slug}`}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm hover:border-primary hover:bg-slate-50"
                  >
                    <span className="font-medium text-slate-900">{t.label}</span>
                    <span className="ml-1 text-xs text-slate-500">{t.priceRange}</span>
                  </Link>
                ))}
              </div>
              <Link
                to={`/tarifs/${s.slug}`}
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                Voir les tarifs {s.artisan} par ville <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </section>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  )
}
